import * as Yup from 'yup';
import TextArea from '@/molecules/TextArea.vue';
import TextInput from '@/molecules/TextInput.vue';
import RadioRowItem from '@/molecules/RadioRowItem.vue';
import defaultValidators, { REQUIRED_ERROR_MESSAGE } from '@/helpers/default-validators';
import { withGlobalFlow } from '@/parcours/flowGlobalSteps';
import { usePreventionParcours } from '@/parcours/composables/usePreventionParcours';
import SpecialistSelector from '@/parcours/components/specialistSelector.vue';
import { MODAL_CONTENT_ENDOMETRIOSIS } from '@/parcours/flows/endometriosis/modalContentEndometriosis';

// Parcours prévention endométriose (mission type « prevention endometriose »).
//
// 1. Les premières étapes reprennent les réponses données par la patiente dans
//    flow-v2 (pré-remplies, modifiables : même principe que le parcours MCV).
// 2. Le pro complète ensuite les questions historiques du parcours (cycle,
//    localisation, symptômes, fertilité) — colonnes déjà présentes dans
//    CoordinatorPatientEndometriosisInfos.
// 3. Recommandations, puis envoi du compte-rendu PDF (POST /cms/send-pdf).
//
// Chaque étape est sauvegardée au fil de l'eau (sideEffect), comme pour MCV.

const { update, getById } = usePreventionParcours('/cms/coordinator-patient-endometriosis-infos');

const YES_NO = [
  { label: 'Oui', value: true },
  { label: 'Non', value: false },
];

const YES_NO_DONT_KNOW = [
  { label: 'Oui', value: 'YES' },
  { label: 'Non', value: 'NO' },
  { label: 'Je ne sais pas', value: 'I_DONT_KNOW' },
];

const TRIGGER_EVENTS = [
  { label: 'Depuis le début de mes menstruations', value: 'SINCE_BEGINNING_OF_MENSTRUATION' },
  { label: 'Depuis un accouchement', value: 'SINCE_CHILDBIRTH' },
  { label: "Pas d'évènement particulier", value: 'NONE_IN_PARTICULAR' },
  { label: 'Autre', value: 'OTHER' },
];

const MOMENTS_OF_PAIN = [
  { label: 'Les jours précédant immédiatement les règles', value: 'DAYS_BEFORE_THE_MENSTRUATION' },
  { label: 'Au moment des règles', value: 'AT_THE_MOMENT_OF_MENSTRUATION' },
  { label: 'Juste après les règles', value: 'JUST_AFTER_OF_MENSTRUATION' },
  { label: 'Au milieu du cycle (12e, 17e jour)', value: 'IN_THE_MIDDLE_OF_MENSTRUATION' },
  { label: 'Aucun en particulier', value: 'NONE_IN_PARTICULAR' },
];

const PAIN_LOCATIONS = [
  { name: 'Douleurs pelviennes', value: 'Douleurs pelviennes' },
  { name: 'Douleurs lors des selles', value: 'Douleurs lors des selles' },
  { name: 'Douleurs lors de la miction', value: 'Douleurs lors de la miction' },
  { name: 'Douleur aux lombaires', value: 'Douleur aux lombaires' },
  { name: 'Pas d’endroit spécifique', value: 'Pas d’endroit spécifique' },
];

const SPECIALISTS = [
  { name: 'Gynécologue', value: 'Gynécologue' },
  { name: 'Médecin généraliste', value: 'Médecin généraliste' },
  { name: 'Sage-femme', value: 'Sage-femme' },
  { name: 'Algologue', value: 'Algologue' },
  { name: 'Autre', value: 'Autre' },
  { name: 'Aucun', value: 'Aucun' },
];

const MANAGEMENT_TECHNIQUES = [
  { name: 'Sophrologie', value: 'Sophrologie' },
  { name: 'Yoga', value: 'Yoga' },
  { name: 'Relaxation', value: 'Relaxation' },
  { name: 'Ostéopathie', value: 'Ostéopathie' },
  { name: 'Hypnose', value: 'Hypnose' },
  { name: 'Kinésithérapie viscérale', value: 'Kinésithérapie viscérale' },
  { name: 'Alimentation anti-inflammatoire', value: 'Alimentation anti-inflammatoire' },
  { name: 'Phytothérapie', value: 'Phytothérapie' },
  { name: 'Acupuncture', value: 'Acupuncture' },
  { name: 'Aucun', value: 'Aucun' },
];

const scale0to10 = Yup.number()
  .typeError('Ce champ doit être un numéro')
  .min(0, 'La valeur doit être comprise entre 0 et 10')
  .max(10, 'La valeur doit être comprise entre 0 et 10')
  .required(REQUIRED_ERROR_MESSAGE);

const days0to99 = Yup.number()
  .typeError('Ce champ doit être un numéro')
  .min(0, 'La valeur doit être comprise entre 0 et 99')
  .max(99, 'La valeur doit être comprise entre 0 et 99')
  .required(REQUIRED_ERROR_MESSAGE);

const radioField = (
  id: string,
  label: string,
  options: { label: string; value: any }[] = YES_NO,
) => ({
  id,
  type: 'radio-group',
  as: RadioRowItem,
  label,
  options,
  optionValue: 'value',
  optionLabel: (opt: any) => opt.label,
  rules: defaultValidators.radioGroup,
});

const numberField = (id: string, label: string, rules: any, hint?: string) => ({
  id,
  type: 'number',
  as: TextInput,
  label,
  ...(hint && { secondIconHint: hint }),
  rules,
});

// Bouton de recommandation : ouvre une modale avec le contenu associé.
const recoAction = ({ title, content }: { title: string; content: string }) => ({
  label: title,
  onClick: async ({ modal }: any) => {
    await modal.openModal({ title, content });
  },
});

// Sauvegarde intermédiaire de l'étape (sans envoi du PDF).
const saveStep = async ({ data }: { data: any }) => {
  await update(data?.id, data, false);
};

export const endometriosisFlow = withGlobalFlow({
  id: 'endometriosisFlow',

  basePath: '/endometriosisFlow/flow',

  exitRoute: '/endometriosisFlow',

  async onInit({ route }) {
    const id = route.params.reservationId;
    const data = await getById(id);
    return { ...data };
  },

  steps: {
    // ── Réponses de la patiente (flow-v2), à vérifier ────────────────────────
    attentes: {
      type: 'form',
      title: 'Attentes de la patiente',
      description:
        'Réponses données par la patiente lors de la réservation : vérifiez-les et complétez si besoin.',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          radioField('expectations', 'Avez-vous d’autres attentes lors de cette séance ?'),
          {
            id: 'other_expectations',
            type: 'textarea',
            as: TextArea,
            label: 'Vos attentes',
            placeholder: 'Décrivez vos attentes…',
            rules: defaultValidators.textOpt,
          },
        ],
      },
    },

    douleur: {
      type: 'form',
      title: 'Échelle de douleur',
      description: 'Réponses de la patiente à vérifier.',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          numberField(
            'pain_scale',
            'Sur une échelle de 0 à 10, comment noteriez-vous la douleur lors de vos règles ? (0 : pas de douleur, 10 : douleur insupportable)',
            scale0to10,
            '/ 10',
          ),
          radioField(
            'drug_treatment',
            'Les douleurs pendant les règles sont-elles calmées par un antalgique ?',
          ),
          numberField(
            'pain_impact',
            'Sur une échelle de 0 à 10, à quel point vos douleurs impactent-elles votre quotidien ?',
            scale0to10,
            '/ 10',
          ),
        ],
      },
      props: {
        actions: [recoAction(MODAL_CONTENT_ENDOMETRIOSIS.PAIN_SCALE)],
      },
    },

    // ── Questions complémentaires du professionnel ───────────────────────────
    cycleMenstruel: {
      type: 'form',
      title: 'Cycle menstruel',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          radioField(
            'trigger_event',
            'Avez-vous commencé à souffrir de ces douleurs après un évènement particulier ?',
            TRIGGER_EVENTS,
          ),
          radioField(
            'moment_of_pain',
            'À quel moment de votre cycle menstruel vos douleurs sont-elles maximales ?',
            MOMENTS_OF_PAIN,
          ),
          numberField(
            'number_of_days_pain',
            'Pendant combien de jours souffrez-vous en moyenne pendant un cycle ?',
            days0to99,
            'jours',
          ),
        ],
      },
      props: {
        actions: [recoAction(MODAL_CONTENT_ENDOMETRIOSIS.MENSTRUAL_CYCLE)],
      },
    },

    localisation: {
      id: 'pain_location',
      type: 'component',
      title: 'Localisation de la douleur',
      description: 'Où se situent ces douleurs ?',
      component: SpecialistSelector,
      props: {
        label: 'Localisation',
        specialists: PAIN_LOCATIONS,
        actions: [recoAction(MODAL_CONTENT_ENDOMETRIOSIS.PAIN_LOCATION)],
      },
    },

    symptomes: {
      type: 'form',
      title: 'Symptômes associés',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          radioField(
            'pain_reports',
            'Ressentez-vous des douleurs dans le bas ventre lors de vos rapports sexuels profonds ?',
          ),
          radioField(
            'pain_faecal',
            'Avez-vous des douleurs en allant à la selle pendant vos règles ?',
          ),
          radioField('pain_blood', 'Avez-vous du sang dans vos selles pendant les règles ?'),
          radioField('pain_urine', 'Avez-vous des douleurs urinaires pendant vos règles ?'),
        ],
      },
      props: {
        actions: [recoAction(MODAL_CONTENT_ENDOMETRIOSIS.DYSPAREUNIA)],
      },
    },

    fertilite: {
      type: 'form',
      title: 'Fertilité',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          radioField(
            'infertility',
            'Avez-vous des difficultés à concevoir un enfant ?',
            YES_NO_DONT_KNOW,
          ),
        ],
      },
      props: {
        actions: [recoAction(MODAL_CONTENT_ENDOMETRIOSIS.INFERTILITY)],
      },
    },

    qualiteDeVie: {
      type: 'form',
      title: 'Qualité de vie et antécédents',
      description: 'Réponses de la patiente à vérifier.',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          radioField('tired', 'Êtes-vous régulièrement fatiguée ?'),
          radioField(
            'absenteeism',
            'Vous arrive-t-il de vous absenter du travail ou de l’école à cause de douleurs pelviennes ?',
          ),
          radioField(
            'family_history',
            'Avez-vous connaissance d’antécédents d’endométriose dans votre famille ? (mère, sœur…)',
          ),
          radioField(
            'nodules_and_cysts',
            'Avez-vous des nodules ou des kystes, ou avez-vous déjà été traitée pour ces symptômes ?',
          ),
        ],
      },
    },

    // ── Recommandations ──────────────────────────────────────────────────────
    recoConsultation: {
      type: 'form',
      title: 'Recommandations',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          radioField(
            'consultation_examen_deepen',
            'Je recommande à la patiente de consulter pour un examen plus approfondi',
          ),
          radioField(
            'treatment_medical_recommendation',
            'Je recommande à la patiente de se rapprocher de son médecin ou de son gynécologue afin d’obtenir un traitement hormonal ou antalgique pour lutter contre la douleur',
          ),
        ],
      },
    },

    recoSpecialistes: {
      id: 'consultation_specialist',
      type: 'component',
      title: 'Recommandations',
      description:
        'Je recommande à la patiente d’entrer en contact avec un ou plusieurs des spécialistes suivants :',
      component: SpecialistSelector,
      props: {
        label: 'Spécialistes',
        specialists: SPECIALISTS,
      },
    },

    recoTechniques: {
      id: 'recommendation_management_technique',
      type: 'component',
      title: 'Recommandations',
      description:
        'Je recommande à la patiente d’avoir recours à une ou plusieurs techniques de gestion de la douleur telles que :',
      component: SpecialistSelector,
      props: {
        label: 'Techniques de gestion de la douleur',
        specialists: MANAGEMENT_TECHNIQUES,
      },
    },

    lastComment: {
      type: 'form',
      title: 'Un dernier commentaire ?',
      description: 'Autres commentaires et recommandations pour la patiente',
      schema: {
        validateOnChange: false,
        validateOnBlur: true,
        fields: [
          {
            id: 'other_recommendation',
            type: 'textarea',
            placeholder: 'Votre commentaire ...',
            as: TextArea,
            rules: defaultValidators.textOpt,
          },
        ],
      },
    },
  },

  transitions: {
    attentes: { sideEffect: saveStep, next: 'douleur' },
    douleur: { sideEffect: saveStep, next: 'cycleMenstruel' },
    cycleMenstruel: { sideEffect: saveStep, next: 'localisation' },
    localisation: { sideEffect: saveStep, next: 'symptomes' },
    symptomes: { sideEffect: saveStep, next: 'fertilite' },
    fertilite: { sideEffect: saveStep, next: 'qualiteDeVie' },
    qualiteDeVie: { sideEffect: saveStep, next: 'recoConsultation' },
    recoConsultation: { sideEffect: saveStep, next: 'recoSpecialistes' },
    recoSpecialistes: { sideEffect: saveStep, next: 'recoTechniques' },
    recoTechniques: { sideEffect: saveStep, next: 'lastComment' },
    lastComment: { submit: true },
  },

  async onSubmit(data) {
    await update(data?.id, { ...data, is_interviewed: true });
  },
});
